import { NgFor, NgIf } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { FormsModule, NgForm, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { DialogService, FeedbackService } from '@app/core/services';
import { NotificationFrequencyEnum } from '@app/enums/system-settings';
import { SelectOption } from '@app/interfaces';
import { NotificationModel, NotificationRecipientUserModel, UserRoleModel } from '@app/models/system-settings';
import { RoleModel } from '@app/models/system-settings/role.model';
import { NgForTrackByIdDirective } from '@app/shared/directives/ng-for-track-by-id.directive';
import { FormComponent } from '@app/shared/forms/components';
import { mapArrayToSelectOptions, mapEnumToSelectOptions } from '@app/utils';
import { ReadOnly } from '@app/utils/claims';
import { NotificationService } from '../../services/notification.service';

const notificationRecipients = 'Notification Recipients';
@Component({
	selector: 'ss-notifications-grid',
	templateUrl: './notifications-grid.component.html',
	styleUrls: ['./notifications-grid.component.less'],
	standalone: true,
	imports: [
		MatExpansionModule,
		NgIf,
		FormsModule,
		MatTableModule,
		MatSlideToggleModule,
		MatFormFieldModule,
		MatSelectModule,
		NgFor,
		NgForTrackByIdDirective,
		MatOptionModule,
		MatButtonModule,
		FormComponent
	],
})
export class NotificationsGridComponent {
	@ViewChild('notificationsForm') notificationsForm: NgForm;
	@ViewChild(FormComponent) formComponent: FormComponent;
	@Input() employee: boolean;
	@Input() expanded = true;
	@ReadOnly()
	isReadOnly = true;

	allUsers: UserRoleModel[] = [];

	fields: FormlyFieldConfig[] = [
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimpleTitle],
			fieldGroup: [
				{
					key: 'Recipients',
					type: FORMLY_FIELD_TYPES.repeat,
					templateOptions: {
						label: notificationRecipients,
						headingText: notificationRecipients,
						addText: 'Add Role',
						showAddOnTop: true,
						removed: () => this.updateRoleOptions(),
					},
					fieldArray: {
						fieldGroup: [
							{
								className: 'col-xs-12',
								type: FORMLY_FIELD_TYPES.select,
								key: 'RoleId',
								templateOptions: {
									label: 'Role',
									required: true,
									change: (field: FormlyFieldConfig): void => this.updateRoleOptions(null, field),
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.Roles',
								},
							},
							{
								className: 'col-xs-12',
								type: FORMLY_FIELD_TYPES.checkbox,
								key: 'AllUsers',
								defaultValue: true,
								templateOptions: {
									label: 'Email All Users in Group',
									required: true,
									appearance: 'standard',
								},
							},
							{
								className: 'col-xs-12',
								type: FORMLY_FIELD_TYPES.select,
								key: 'UserIds',
								templateOptions: {
									label: 'Individuals',
									required: true,
									multiple: true,
								},
								expressionProperties: {
									'templateOptions.options': (model: any, formState: any) =>
										formState.selects.AllUsersList.filter((x) => x.RoleId === model.RoleId),
									'templateOptions.disabled': (model) => model.AllUsers,
								},
								hideExpression: 'model.AllUsers',
							},
						],
					},
				},
			],
		},
	];

	options: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			readOnly: false,
			flyout: true,
			selects: {
				Roles: [],
				Users: [],
				AllUsers: [],
				AllUsersList: [],
			},
		},
	};

	form = new UntypedFormGroup({});
	internalNotifications: NotificationModel[] = [];
	rolesSelectOptions: SelectOption[];
	roleUserMap: Map<string, any[]>;
	selectedModel: NotificationModel;

	get notifications(): NotificationModel[] {
		return this.internalNotifications;
	}

	@Input() set notifications(value: NotificationModel[]) {
		this.internalNotifications = value;
		this.originalNotifications = cloneDeep(this.internalNotifications);
	}

	@Input() set roles(roles: RoleModel[]) {
		this.rolesSelectOptions = mapArrayToSelectOptions(roles, {
			value: 'Id',
			label: 'Name',
			all: false,
		});
		this.options.formState.selects.Roles = cloneDeep(this.rolesSelectOptions);
		this.roleUserMap = new Map<string, any[]>(roles.map((r) => [r.Id, r.Users]));
		this.allUsers = this.getAllUsersIntoList(roles);
		this.options.formState.selects.AllUsersList = cloneDeep(this.allUsers);
		const allUsersSelectOptions = mapArrayToSelectOptions(this.getAllUsersIntoList(roles), {
			value: 'Id',
			label: 'Name',
			sort: 'label',
			order: 'desc',
			all: false,
		});
		this.options.formState.selects.AllUsers = cloneDeep(allUsersSelectOptions);
	}

	originalNotifications: NotificationModel[] = [];
	frequencies: SelectOption[] = mapEnumToSelectOptions(NotificationFrequencyEnum).filter((o) => o.value);

	constructor(
		private readonly notificationService: NotificationService,
		private readonly feedbackService: FeedbackService,
		private readonly dialogService: DialogService
	) {}

	columnsToDisplay(): string[] {
		if (this.employee) {
			return ['Description', 'Email'];
		} else {
			return ['Description', 'Email', 'Recipients'];
		}
	}

	save(): void {
		this.notificationService.update(this.notifications).subscribe(() => {
			this.notificationsForm.form.markAsPristine();
			this.notificationsForm.form.markAsUntouched();
			this.originalNotifications = cloneDeep(this.notifications);

			this.feedbackService.success('saved', 'Notification Settings', { isPlural: true });
		});
	}

	cancel(): void {
		this.notificationsForm.form.markAsPristine();
		this.notificationsForm.form.markAsUntouched();
		this.internalNotifications = cloneDeep(this.originalNotifications);
	}

	public CanDeactivate(): boolean {
		return !this.notificationsForm.dirty;
	}

	onSubmit(model: NotificationModel): void {
		if (model.Recipients) {
			model.Recipients.forEach((x) => {
				if (!x.AllUsers && x.UserIds) {
					x.Users = [];
					x.UserIds.forEach((y) => {
						const newUser = {
							UserId: y,
						} as NotificationRecipientUserModel;
						x.Users.push(newUser);
					});
				}
			});
		} else {
			model.Recipients = [];
		}
		if (model.Recipients.length === 0) {
			this.dialogService
				.open({
					title: 'Notification Save Warning',
					message: 'There are no recipients selected for this notification. Do you wish to continue?',
					okText: 'Continue',
					cancelText: 'Cancel',
				})
				.subscribe((result) => {
					if (result) {
						this.updateNotifications(model);
					}
				});
		} else {
			this.updateNotifications(model);
		}
	}

	updateNotifications(model: NotificationModel): void {
		this.notificationService.updateNotification(model).subscribe((response: NotificationModel) => {
			this.feedbackService.success('saved', notificationRecipients);
			this.formComponent.editForm.markAsPristine();
			this.formComponent.editForm.markAsUntouched();
			this.formComponent.closeFlyout();
			const matchedNotification = this.notifications.find((x) => x.Id === response.Id);
			matchedNotification.Recipients = response.Recipients;
		});
	}

	openFlyout(model: NotificationModel): void {
		if (this.isReadOnly) {
			return;
		}
		if (model.Recipients) {
			model.Recipients.forEach((x) => {
				if (x.Users) {
					x.UserIds = x.Users.map((y) => y.UserId);
				}
			});
		}
		if (model.AllUsers === null || model.AllUsers === undefined) {
			model.AllUsers = false;
		}
		this.formComponent.entityId = model.Id;
		this.formComponent.model = model;
		this.formComponent.edit();
		this.formComponent.flyoutTitle = `Edit ${model.Description} Recipients`;
		this.updateRoleOptions(model);
	}

	private getAllUsersIntoList(roles: RoleModel[]): UserRoleModel[] {
		let users: UserRoleModel[] = [];

		if (roles) {
			roles.forEach((x) => {
				if (x.Users) {
					x.Users.forEach((y) => {
						const user = {
							Id: y.Id,
							Name: y.Name,
							RoleId: x.Id,
							value: y.Id,
							label: y.Name,
						} as UserRoleModel;
						users.push(user);
					});
				}
			});
		}

		users = users.sort((a, b) => {
			const fa = a.Name.toLowerCase();
			const fb = b.Name.toLowerCase();

			if (fa < fb) {
				return -1;
			}
			if (fa > fb) {
				return 1;
			}
			return 0;
		});
		return users;
	}

	private updateRoleOptions(initialModel?: NotificationModel, field?: FormlyFieldConfig): void {
		if (field) {
			field.form.get('UserIds')?.setValue([]);
		}
		const model = initialModel ? initialModel : ({ ...this.form.value } as NotificationModel);
		const selectedRoleIds = model.Recipients ? model.Recipients.map((x) => x.RoleId) : [];
		this.options.formState.selects.Roles = this.options.formState.selects.Roles.map((x) => {
			if (selectedRoleIds.includes(x.value)) {
				return { ...x, disabled: 'true' };
			} else {
				return { ...x, disabled: 'false' };
			}
		});
	}
}
